/**
 * LERA Device Engine — Business layer and public API.
 * window.LERA.compatibility
 */
(function (global) {
  'use strict';

  var LERA = (global.LERA = global.LERA || {});
  var debug = LERA._debug;
  var ENGINE_SCOPE = 'Engine';

  function getPipelineModules() {
    var modules = LERA._modules || {};
    return {
      catalogProvider: modules.catalogProvider,
      aliasResolver: modules.aliasResolver,
      deviceIndex: modules.deviceIndex,
    };
  }

  function getCatalogProvider() {
    return getPipelineModules().catalogProvider;
  }

  var CONTEXT_KEY = 'lera:device-context';
  var GLOBAL_REDIRECT_COLLECTION = 'all';
  /** DCF-002: stable device marker carried in navigation URLs (read by DCF-003). */
  var DEVICE_URL_PARAM = 'lera_device';

  var engineState = {
    configured: false,
    config: null,
    aliasResolver: null,
    deviceIndex: null,
    globalLoadPromise: null,
    bootPromise: null,
    diagnostics: {
      initialized: false,
      warnings: [],
      success: [],
      providerStatus: 'idle',
      cacheStatus: 'idle',
      brandsFound: 0,
      modelsIndexed: 0,
      aliasesLoaded: 0,
      productsRead: 0,
      variantsRead: 0,
      currentDevice: null,
      redirectMode: null,
      catalogSource: 'global',
      cacheAgeMinutes: null,
      globalIndexBuilt: false,
      searchReady: false,
    },
  };

  function slugifyOptionName(name) {
    return String(name || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function getBrandRecord(brandId) {
    var brands = (engineState.config && engineState.config.brands) || [];
    for (var index = 0; index < brands.length; index += 1) {
      if (brands[index].id === brandId) {
        return brands[index];
      }
    }
    return null;
  }

  function getRedirectCollectionHandle(brandRecord) {
    if (brandRecord && brandRecord.collectionHandle) {
      return brandRecord.collectionHandle;
    }

    if (engineState.config && engineState.config.redirectCollectionHandle) {
      return engineState.config.redirectCollectionHandle;
    }

    return GLOBAL_REDIRECT_COLLECTION;
  }

  function pushDiagnostic(type, message) {
    if (!engineState.config || !engineState.config.enableDiagnostics) return;
    engineState.diagnostics[type].push(message);
  }

  function readDeviceContext() {
    try {
      var raw = sessionStorage.getItem(CONTEXT_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function writeDeviceContext(device) {
    try {
      if (!device) {
        sessionStorage.removeItem(CONTEXT_KEY);
        return;
      }
      sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(device));
    } catch (error) {
      /* ignore */
    }
  }

  function appendQueryParam(url, name, value) {
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator + name + '=' + encodeURIComponent(value);
  }

  /**
   * DCF-002: every navigation URL carries the exact "Dispositivo compatible"
   * option value as a stable `lera_device` parameter, regardless of redirect
   * mode. The URL is deterministic and shareable for a given brand + model,
   * and DCF-003 can resolve the device from the URL alone (no session needed).
   */
  function buildRedirectUrl(brandRecord, modelValue) {
    var config = engineState.config || {};
    var mode = config.redirectMode || 'collection';
    var collectionsRoot = config.collectionsRoot || '/collections/';
    var collectionHandle = getRedirectCollectionHandle(brandRecord);
    var optionName = config.optionName || 'Dispositivo compatible';

    var base = collectionsRoot;
    if (base.charAt(base.length - 1) !== '/') {
      base += '/';
    }
    base += encodeURIComponent(collectionHandle);

    var url = base;

    if (mode === 'option_filter' && modelValue) {
      var optionSlug = slugifyOptionName(optionName);
      url = appendQueryParam(base, 'filter.v.option.' + encodeURIComponent(optionSlug), modelValue);
    } else if (mode === 'search_discovery' && modelValue) {
      url = appendQueryParam(base, 'q', modelValue) + '&type=product';
    } else if (mode === 'search' && modelValue) {
      url = '/search?q=' + encodeURIComponent(modelValue) + '&type=product';
    }

    if (modelValue) {
      url = appendQueryParam(url, DEVICE_URL_PARAM, modelValue);
    }

    return url;
  }

  function ensureConfigured() {
    if (!engineState.configured) {
      throw new Error('LERA Device Engine is not configured.');
    }
  }

  function validatePipelineModules() {
    var pipelineModules = getPipelineModules();
    var catalogProvider = pipelineModules.catalogProvider;

    if (!catalogProvider) {
      return { valid: false, reason: 'CatalogProvider module not loaded', missing: 'CatalogProvider' };
    }

    if (typeof catalogProvider.fetchGlobalCatalog !== 'function') {
      return {
        valid: false,
        reason: 'CatalogProvider.fetchGlobalCatalog is unavailable',
        missing: 'CatalogProvider.fetchGlobalCatalog',
      };
    }

    if (!pipelineModules.aliasResolver) {
      return { valid: false, reason: 'AliasResolver module not loaded', missing: 'AliasResolver' };
    }

    if (!pipelineModules.deviceIndex) {
      return { valid: false, reason: 'DeviceIndex module not loaded', missing: 'DeviceIndex' };
    }

    return { valid: true, reason: null, missing: null };
  }

  function resetPipelinePromises() {
    engineState.globalLoadPromise = null;
    engineState.bootPromise = null;
    engineState.diagnostics.providerStatus = 'idle';
    engineState.diagnostics.globalIndexBuilt = false;
    engineState.diagnostics.searchReady = false;
  }

  function applyCatalogDiagnostics(catalogResult) {
    engineState.diagnostics.productsRead = catalogResult.meta.productCount || 0;
    engineState.diagnostics.variantsRead = catalogResult.meta.variantCount || 0;
    engineState.diagnostics.cacheStatus = catalogResult.meta.fromCache ? 'hit' : 'miss';
    engineState.diagnostics.catalogSource = catalogResult.meta.source || 'global';

    if (catalogResult.meta.cachedAt) {
      engineState.diagnostics.cacheAgeMinutes = Math.round((Date.now() - catalogResult.meta.cachedAt) / 60000);
    }
  }

  var publicApi = {
    init: function (config) {
      engineState.config = config || {};
      resetPipelinePromises();

      if (debug) {
        debug.configure(engineState.config);
      }

      if (debug && debug.isEnabled()) {
        debug.log(ENGINE_SCOPE, 'init started', {
          optionName: engineState.config.optionName,
          brandCount: (engineState.config.brands || []).length,
          redirectMode: engineState.config.redirectMode,
          catalogSource: engineState.config.catalogSource || 'global',
        });
      }

      var moduleCheck = validatePipelineModules();
      if (!moduleCheck.valid) {
        engineState.configured = true;
        engineState.diagnostics.initialized = true;
        engineState.diagnostics.providerStatus = 'error';
        pushDiagnostic('warnings', moduleCheck.reason);

        if (debug && debug.isEnabled()) {
          debug.error(ENGINE_SCOPE, 'Pipeline aborted during init — module missing', moduleCheck.reason, {
            missing: moduleCheck.missing,
          });
        }

        if (debug && debug.isEnabled()) {
          debug.log(ENGINE_SCOPE, 'init complete', {
            pipelineStarted: false,
            missing: moduleCheck.missing,
          });
        }

        return publicApi;
      }

      var pipelineModules = getPipelineModules();
      engineState.aliasResolver = new pipelineModules.aliasResolver({
        customAliases: engineState.config.customAliases || '',
      });
      engineState.deviceIndex = new pipelineModules.deviceIndex(engineState.aliasResolver);
      engineState.configured = true;
      engineState.diagnostics.initialized = true;
      engineState.diagnostics.brandsFound = (engineState.config.brands || []).filter(function (brand) {
        return brand.enabled;
      }).length;
      engineState.diagnostics.aliasesLoaded = engineState.aliasResolver.diagnostics.aliasesLoaded;
      engineState.diagnostics.redirectMode = engineState.config.redirectMode || 'option_filter';
      engineState.diagnostics.catalogSource = 'global';
      engineState.diagnostics.currentDevice = readDeviceContext();
      pushDiagnostic('success', 'Engine initialized.');

      if (debug && debug.isEnabled()) {
        debug.log(ENGINE_SCOPE, 'init complete', {
          brandsFound: engineState.diagnostics.brandsFound,
          aliasesLoaded: engineState.diagnostics.aliasesLoaded,
          pipelineStarted: true,
        });
      }

      if (debug && debug.isEnabled()) {
        debug.log(ENGINE_SCOPE, 'Provider modules validated', {
          catalogProvider: true,
          aliasResolver: true,
          deviceIndex: true,
        });
        debug.log(ENGINE_SCOPE, 'Provider modules validated — starting bootPipeline');
      }

      publicApi.bootPipeline();

      return publicApi;
    },

    bootPipeline: function () {
      ensureConfigured();

      if (engineState.bootPromise) {
        if (debug && debug.isEnabled()) {
          debug.log(ENGINE_SCOPE, 'bootPipeline — returning existing boot promise');
        }
        return engineState.bootPromise;
      }

      var moduleCheck = validatePipelineModules();
      if (!moduleCheck.valid) {
        engineState.diagnostics.providerStatus = 'error';

        if (debug && debug.isEnabled()) {
          debug.error(ENGINE_SCOPE, 'bootPipeline aborted — module missing', moduleCheck.reason, {
            missing: moduleCheck.missing,
          });
        }

        return Promise.resolve({
          success: false,
          reason: 'provider_missing',
          errorMessage: moduleCheck.reason,
          missing: moduleCheck.missing,
        });
      }

      if (debug && debug.isEnabled()) {
        debug.log(ENGINE_SCOPE, 'bootPipeline started', {
          step: 'initialize Provider → fetchGlobalCatalog → buildGlobalIndex → brand views',
        });
      }

      engineState.bootPromise = publicApi
        .loadGlobalIndex()
        .then(function (result) {
          engineState.diagnostics.searchReady = Boolean(result.success);

          if (result.success) {
            pushDiagnostic('success', 'Search engine ready.');

            if (debug && debug.isEnabled()) {
              debug.log(ENGINE_SCOPE, 'Search Engine Ready', {
                globalModelCount: result.modelCount,
                brandViews: engineState.deviceIndex.getBrands(engineState.config.brands || []),
              });
              debug.log(ENGINE_SCOPE, 'bootPipeline complete — search engine exposed', {
                globalModelCount: result.modelCount,
                brandViews: engineState.deviceIndex.getBrands(engineState.config.brands || []),
              });
            }
          } else {
            engineState.bootPromise = null;

            if (debug && debug.isEnabled()) {
              debug.error(ENGINE_SCOPE, 'bootPipeline finished with failure', result.reason, result);
            }
          }

          return result;
        })
        .catch(function (error) {
          engineState.diagnostics.searchReady = false;
          engineState.bootPromise = null;

          if (debug && debug.isEnabled()) {
            debug.error(ENGINE_SCOPE, 'bootPipeline exception', error);
          }

          return {
            success: false,
            reason: 'boot_exception',
            errorMessage: error && error.message ? error.message : String(error),
          };
        });

      return engineState.bootPromise;
    },

    whenReady: function () {
      ensureConfigured();

      if (engineState.deviceIndex && engineState.deviceIndex.isGlobalBuilt()) {
        return Promise.resolve({
          success: true,
          reason: 'already_indexed',
          modelCount: engineState.deviceIndex.getGlobalStats().modelCount,
        });
      }

      return publicApi.bootPipeline();
    },

    loadGlobalIndex: function () {
      ensureConfigured();

      if (engineState.deviceIndex.isGlobalBuilt()) {
        if (debug && debug.isEnabled()) {
          debug.log(ENGINE_SCOPE, 'loadGlobalIndex skipped — already built', engineState.deviceIndex.getGlobalStats());
        }

        return Promise.resolve({
          success: true,
          reason: 'already_indexed',
          modelCount: engineState.deviceIndex.getGlobalStats().modelCount,
        });
      }

      if (engineState.globalLoadPromise) {
        if (debug && debug.isEnabled()) {
          debug.log(ENGINE_SCOPE, 'loadGlobalIndex awaiting in-flight request');
        }
        return engineState.globalLoadPromise;
      }

      engineState.diagnostics.providerStatus = 'loading';

      if (debug && debug.isEnabled()) {
        debug.log(ENGINE_SCOPE, 'loadGlobalIndex started — calling Provider.fetchGlobalCatalog', {
          optionName: engineState.config.optionName,
          cacheEnabled: engineState.config.cacheEnabled !== false,
          cacheTtlMs: engineState.config.cacheTtlMs,
        });
      }

      if (!getCatalogProvider() || typeof getCatalogProvider().fetchGlobalCatalog !== 'function') {
        engineState.diagnostics.providerStatus = 'error';
        engineState.globalLoadPromise = null;

        if (debug && debug.isEnabled()) {
          debug.error(ENGINE_SCOPE, 'loadGlobalIndex aborted — CatalogProvider unavailable', null, {
            missing: 'CatalogProvider',
          });
        }

        return Promise.resolve({
          success: false,
          userMessage: engineState.config.messages && engineState.config.messages.error,
          reason: 'provider_missing',
        });
      }

      var catalogProvider = getCatalogProvider();

      engineState.globalLoadPromise = catalogProvider.fetchGlobalCatalog({
        optionName: engineState.config.optionName,
        cacheEnabled: engineState.config.cacheEnabled !== false,
        cacheTtlMs: engineState.config.cacheTtlMs,
      })
        .then(function (catalogResult) {
          engineState.globalLoadPromise = null;

          if (!catalogResult.success) {
            engineState.diagnostics.providerStatus = 'error';
            pushDiagnostic('warnings', 'Global catalog provider error.');

            if (debug && debug.isEnabled()) {
              debug.error(ENGINE_SCOPE, 'Provider returned failure', catalogResult.error && catalogResult.error.message, catalogResult.error);
            }

            return {
              success: false,
              userMessage: engineState.config.messages && engineState.config.messages.error,
              reason: 'provider_error',
              error: catalogResult.error,
            };
          }

          applyCatalogDiagnostics(catalogResult);

          if (debug && debug.isEnabled()) {
            debug.log(ENGINE_SCOPE, 'Provider success — building Device Index', {
              productCount: catalogResult.meta.productCount,
              variantCount: catalogResult.meta.variantCount,
              fromCache: catalogResult.meta.fromCache || false,
            });
          }

          var buildResult = engineState.deviceIndex.buildGlobalIndex(
            catalogResult,
            engineState.config.optionName,
            engineState.config.brands || []
          );

          engineState.diagnostics.providerStatus = 'ready';
          engineState.diagnostics.globalIndexBuilt = true;
          engineState.diagnostics.modelsIndexed = buildResult.globalStats.modelCount;

          if (!buildResult.globalStats.optionFound) {
            pushDiagnostic('warnings', 'Configured option does not exist: ' + engineState.config.optionName);
          }

          if (!buildResult.globalStats.modelCount) {
            pushDiagnostic('warnings', 'No compatible device models found in global catalog.');

            if (debug && debug.isEnabled()) {
              debug.warn(ENGINE_SCOPE, 'Indexing finished with zero models', buildResult.globalStats);
            }

            return {
              success: false,
              userMessage: engineState.config.messages && engineState.config.messages.empty,
              reason: 'no_models',
            };
          }

          pushDiagnostic('success', 'Global catalog indexed.');

          if (debug && debug.isEnabled()) {
            debug.log(ENGINE_SCOPE, 'loadGlobalIndex complete', {
              globalModelCount: buildResult.globalStats.modelCount,
              productsRead: engineState.diagnostics.productsRead,
              variantsRead: engineState.diagnostics.variantsRead,
            });
          }

          return {
            success: true,
            modelCount: buildResult.globalStats.modelCount,
            reason: 'indexed',
          };
        })
        .catch(function (error) {
          engineState.globalLoadPromise = null;
          engineState.diagnostics.providerStatus = 'error';

          if (debug && debug.isEnabled()) {
            debug.error(ENGINE_SCOPE, 'loadGlobalIndex exception', error);
          }

          return {
            success: false,
            userMessage: engineState.config.messages && engineState.config.messages.error,
            reason: 'provider_exception',
            errorMessage: error && error.message ? error.message : String(error),
          };
        });

      return engineState.globalLoadPromise;
    },

    getBrands: function () {
      ensureConfigured();
      return engineState.deviceIndex.getBrands(engineState.config.brands || []);
    },

    loadBrandIndex: function (brandId) {
      ensureConfigured();

      var brandRecord = getBrandRecord(brandId);
      if (!brandRecord) {
        if (debug && debug.isEnabled()) {
          debug.warn(ENGINE_SCOPE, 'loadBrandIndex — brand not found', { brandId: brandId });
        }

        return Promise.resolve({
          success: false,
          userMessage: engineState.config.messages && engineState.config.messages.empty,
          reason: 'brand_not_found',
        });
      }

      if (debug && debug.isEnabled()) {
        debug.log(ENGINE_SCOPE, 'loadBrandIndex started', {
          brandId: brandId,
          brandName: brandRecord.name,
        });
      }

      return publicApi.loadGlobalIndex().then(function (globalResult) {
        if (!globalResult.success) {
          if (debug && debug.isEnabled()) {
            debug.error(ENGINE_SCOPE, 'loadBrandIndex aborted — global index failed', globalResult.reason, globalResult);
          }
          return globalResult;
        }

        var brandIndex = engineState.deviceIndex.getBrandIndex(brandId);
        if (!brandIndex || !brandIndex.stats.modelCount) {
          pushDiagnostic('warnings', 'Brand has no compatible models: ' + brandRecord.name);

          if (debug && debug.isEnabled()) {
            debug.warn(ENGINE_SCOPE, 'Brand has zero models after global index', {
              brandName: brandRecord.name,
              brandId: brandId,
            });
          }

          return {
            success: false,
            userMessage: engineState.config.messages && engineState.config.messages.empty,
            reason: 'no_models',
          };
        }

        pushDiagnostic('success', 'Brand models ready: ' + brandRecord.name);

        if (debug && debug.isEnabled()) {
          debug.log(ENGINE_SCOPE, 'loadBrandIndex complete', {
            brandName: brandRecord.name,
            modelCount: brandIndex.stats.modelCount,
          });
        }

        return {
          success: true,
          modelCount: brandIndex.stats.modelCount,
          reason: 'brand_ready',
        };
      });
    },

    getModels: function (brandId) {
      ensureConfigured();
      return engineState.deviceIndex.getBrandModels(brandId);
    },

    searchModels: function (brandId, query, options) {
      ensureConfigured();
      var searchOptions = {
        searchMode: (options && options.searchMode) || engineState.config.searchMode || 'contains',
        limit: (options && options.limit) || 12,
      };

      var result = engineState.deviceIndex.searchModels(brandId, query, searchOptions);
      var messages = engineState.config.messages || {};

      if (result.reason === 'index_missing') {
        return {
          success: false,
          results: [],
          userMessage: messages.searching || messages.loading,
          reason: result.reason,
        };
      }

      if (!result.results.length) {
        return {
          success: true,
          results: [],
          userMessage: messages.noResults || messages.empty,
          reason: 'no_results',
        };
      }

      return {
        success: true,
        results: result.results,
        userMessage: null,
        reason: result.reason,
      };
    },

    resolveAlias: function (brandName) {
      ensureConfigured();
      return engineState.aliasResolver.resolveAlias(brandName);
    },

    getCurrentDevice: function () {
      return readDeviceContext();
    },

    setCurrentDevice: function (device) {
      if (engineState.config && engineState.config.enableSessionContext === false) {
        return null;
      }

      var payload = {
        brandId: device.brandId,
        brand: device.brand,
        model: device.model,
        source: device.source || 'finder',
        timestamp: Date.now(),
      };

      writeDeviceContext(payload);
      engineState.diagnostics.currentDevice = payload;
      pushDiagnostic('success', 'Context active: ' + payload.brand + ' / ' + payload.model);
      return payload;
    },

    clearCurrentDevice: function () {
      writeDeviceContext(null);
      engineState.diagnostics.currentDevice = null;
      pushDiagnostic('success', 'Context cleared.');
    },

    hasCurrentDevice: function () {
      return Boolean(readDeviceContext());
    },

    /**
     * DCF-002 (infrastructure for DCF-003): resolves the device model from a
     * navigation URL produced by buildRedirect/buildNavigation. Works without
     * session context, so device URLs are shareable across devices/browsers.
     * @param {string} [search] - Query string; defaults to location.search.
     * @returns {{ model: string, param: string } | null}
     */
    getDeviceFromUrl: function (search) {
      var query = typeof search === 'string' ? search : (global.location && global.location.search) || '';
      if (!query) return null;

      if (query.charAt(0) === '?') {
        query = query.slice(1);
      }

      var pairs = query.split('&');
      for (var index = 0; index < pairs.length; index += 1) {
        var pair = pairs[index].split('=');
        if (pair[0] === DEVICE_URL_PARAM && pair[1]) {
          try {
            return {
              model: decodeURIComponent(pair[1].replace(/\+/g, ' ')),
              param: DEVICE_URL_PARAM,
            };
          } catch (error) {
            return null;
          }
        }
      }

      return null;
    },

    buildNavigation: function (intent) {
      ensureConfigured();
      var brandName = intent && intent.brand;
      var modelName = intent && intent.model;
      var brands = engineState.config.brands || [];
      var brandRecord = null;

      for (var index = 0; index < brands.length; index += 1) {
        if (brands[index].name === brandName || brands[index].id === intent.brandId) {
          brandRecord = brands[index];
          break;
        }
      }

      if (!brandRecord) {
        return {
          success: false,
          url: null,
          reason: 'brand_not_found',
        };
      }

      return {
        success: true,
        url: buildRedirectUrl(brandRecord, modelName),
        brand: brandRecord.name,
        model: modelName || null,
        reason: 'resolved',
      };
    },

    buildRedirect: function (brandId, modelValue) {
      ensureConfigured();
      var brandRecord = getBrandRecord(brandId);
      if (!brandRecord || !modelValue) {
        return {
          success: false,
          url: null,
          reason: 'invalid_selection',
        };
      }

      return {
        success: true,
        url: buildRedirectUrl(brandRecord, modelValue),
        reason: 'resolved',
      };
    },

    /**
     * LCE-002 — products compatible with a device model (same catalog scan).
     * @param {string} model
     * @returns {{ success: boolean, productIds: number[], handles: string[], model: string|null, reason: string }}
     */
    getProductsForDevice: function (model) {
      ensureConfigured();
      if (!model) {
        return {
          success: false,
          productIds: [],
          handles: [],
          model: null,
          reason: 'invalid_model',
        };
      }

      return engineState.deviceIndex.getProductsForDevice(model);
    },

    getCompatibleProducts: function () {
      return {
        success: false,
        products: [],
        reason: 'not_implemented',
      };
    },

    getCompatibleCollections: function (brandId) {
      ensureConfigured();
      var brandRecord = getBrandRecord(brandId);
      if (!brandRecord) {
        return { success: false, collections: [], reason: 'brand_not_found' };
      }

      return {
        success: true,
        collections: [
          {
            handle: getRedirectCollectionHandle(brandRecord),
            title: brandRecord.name,
          },
        ],
        reason: 'resolved',
      };
    },

    refreshCache: function () {
      var catalogProvider = getCatalogProvider();
      if (catalogProvider && typeof catalogProvider.clearCache === 'function') {
        catalogProvider.clearCache(engineState.config && engineState.config.optionName);
      }

      if (engineState.deviceIndex && typeof engineState.deviceIndex.clear === 'function') {
        engineState.deviceIndex.clear();
      }

      resetPipelinePromises();
      pushDiagnostic('success', 'Global cache refreshed.');

      if (debug && debug.isEnabled()) {
        debug.log(ENGINE_SCOPE, 'Cache cleared — restarting bootPipeline');
      }

      return publicApi.bootPipeline();
    },

    getDiagnostics: function () {
      return JSON.parse(JSON.stringify(engineState.diagnostics));
    },

    getGlobalStats: function () {
      ensureConfigured();
      return engineState.deviceIndex.getGlobalStats();
    },
  };

  LERA.compatibility = publicApi;
})(typeof window !== 'undefined' ? window : this);
